import React, { useState, useCallback } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OrganizationFieldInput from './organization-field-input';
import { buildSavePayload, isFieldValueEmpty } from './helpers';

/**
 * Uma linha editável de campo personalizado: o input (via motor) + o ícone de disquete que salva
 * AQUELE campo isoladamente.
 *
 * PERFORMANCE (requisito: organizações com 40+ campos): o valor vive em estado LOCAL desta linha e o
 * componente é memoizado. Digitar aqui NÃO re-renderiza as outras 39 linhas nem o modal — que era o
 * que travaria a digitação se tudo pendurasse num state único no pai.
 *
 * Não usa async/await de propósito (Promise chain) — o babel antigo do react-scripts 4 quebra o build
 * em algumas formas de arrow + async; ver .claude/rules/frontend-react.md.
 */
function OrganizationFieldRow(props) {
  const { field, initialValue, calledId, required, disabled, onSave, onSaved, intl } = props;

  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [dirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = useCallback((next) => {
    setValue(next);
    setDirty(true);
    setStatus('idle');
    setErrorMessage('');
  }, []);

  const handleSave = useCallback(() => {
    // Campo obrigatório não pode ser salvo em branco (o backend também bloqueia).
    if (required && isFieldValueEmpty(field, value)) {
      setStatus('error');
      setErrorMessage(intl.formatMessage({ id: 'quicktreat.field.required.empty' }));
      return;
    }

    setStatus('saving');
    setErrorMessage('');

    Promise.resolve(onSave(buildSavePayload(calledId, field, value)))
      .then((result) => {
        if (result && result.success === false) {
          setStatus('error');
          setErrorMessage(result.message || intl.formatMessage({ id: 'process.error' }));
          return;
        }
        setStatus('saved');
        setDirty(false);
        // Notifica o pai do valor efetivamente PERSISTIDO deste campo, para que o modal saiba,
        // no encerramento, quais obrigatórios foram de fato preenchidos (o estado de cada linha
        // é local; o modal só conhece o que foi salvo).
        if (onSaved) onSaved(field.id, value);
      })
      .catch(() => {
        setStatus('error');
        setErrorMessage(intl.formatMessage({ id: 'process.error' }));
      });
  }, [field, value, required, calledId, onSave, onSaved, intl]);

  const renderActionIcon = () => {
    if (status === 'saving') return <CircularProgress size={20} />;
    if (status === 'saved' && !dirty) {
      return (
        <Tooltip title={intl.formatMessage({ id: 'quicktreat.field.saved' })} arrow>
          <CheckCircleIcon style={{ color: '#8BC34A' }} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={intl.formatMessage({ id: 'save' })} arrow>
        {/* span: o Tooltip precisa de um filho que aceite ref mesmo com o botão desabilitado */}
        <span>
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={disabled || !dirty}
            style={{ color: dirty && !disabled ? '#277afe' : '#bdbdbd' }}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 6,
        border: '1px solid #ececec',
        borderLeft: `3px solid ${status === 'error' ? '#CE1B1B' : status === 'saved' ? '#8BC34A' : '#e0e0e0'}`,
        backgroundColor: '#fff',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <OrganizationFieldInput
          field={field}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          required={required} />
        {status === 'error' && errorMessage ? (
          <Typography style={{ fontSize: 12, color: '#CE1B1B', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ErrorOutlineIcon style={{ fontSize: 14 }} />
            {errorMessage}
          </Typography>
        ) : null}
      </div>
      <div style={{ flexShrink: 0, paddingTop: 8 }}>{renderActionIcon()}</div>
    </Box>
  );
}

// Memoizado: o pai re-renderizar (ex.: troca de aba) não re-renderiza cada linha à toa.
export default React.memo(OrganizationFieldRow);
